import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from "rxjs";
import { Select, ofActionSuccessful } from "@ngxs/store";
import { TranslateModule } from "@ngx-translate/core";

import { DataGridComponent } from "@app/shared/components/smart-data-grid/data-grid/data-grid.component";
import { AppDataState } from "@app/shared/stores/app-data.state";
import { DeleteKpiITToolUsage, GetKpiITToolUsage, ImportKpiITToolUsageData } from "@app/shared/actions/app-data.action";
import { ExcelSheetName, NgxsActionName, NgxsFormPath } from "@app/shared/constants";
import { SearchByItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { KpiITToolUsage } from "@app/shared/classes/kpi-it-tool-usage";
import { AngularMaterialModule } from "@app/shared/modules/angular-material.module";
import { LoadingSpinnerComponent } from "@app/shared/components/loading-spinner/loading-spinner.component";
import { CommonModalDialogComponent } from "@app/shared/components/common-modal-dialog/common-modal-dialog.component";
import { SmartSearchFormComponent } from "@app/shared/components/smart-search-form/smart-search-form.component";
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from "@app/shared/components/common-modal-dialog/common-modal-dialog.constant";
import { ITToolUsageEditModalDialogComponent } from "@app/shared/components/edit-modal-dialog/it-tool-usage-edit-modal-dialog/it-tool-usage-edit-modal-dialog.component";
import * as AppDataAction from "@app/shared/actions/app-data.action";
import { DataGridColumnDef, GetITToolUsageSearchByList } from "./it-tools-usage.constant";
import { MessageDataComponent } from "@app/shared/components/message-data/message-data.component";
import { SpinnerButtonComponent } from "@app/shared/components/spinner-button/spinner-button.component";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { ImportDataResultComponent } from "@app/pages/import-data-result/import-data-result.component";
import { ImportResult } from "@app/shared/classes/import-result";
import { CommonPageFilterSetting, CommonSearchCriteria, CreatePageFilterSettingWithLstChangeOnSort } from "@app/shared/models/common-data.model";
import { SiteConfigHelper } from "@app/shared/utils/site-config.helper";
import { ExcelImportButtonComponent } from "@app/shared/components/excel-import-button/excel-import-button.component";
import { CsvImportType } from "@app/shared/enums";
import { DisableDeleteButtonDirective } from "@app/shared/directive/disable-delete-button.directive";
import { DisableEditButtonDirective } from "@app/shared/directive/disable-edit-button.directive";

@Component({
  selector: "app-it-tools-usage",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingSpinnerComponent,
    DataGridComponent,
    MessageDataComponent,
    ITToolUsageEditModalDialogComponent,
    CommonModule,
    TranslateModule,
    DisableDeleteButtonDirective,
    DisableEditButtonDirective,
    AngularMaterialModule,
    SmartSearchFormComponent,
    SpinnerButtonComponent,
    ImportDataResultComponent,
    ExcelImportButtonComponent,
  ],
  templateUrl: "./it-tools-usage.component.html",
  styleUrl: "./it-tools-usage.component.css",
})
export class ItToolsUsageComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.KPI_IT_TOOL_USAGE_SEARCH_FORM;
  selectedItems: KpiITToolUsage[] = [];

  dataSource$: Observable<KpiITToolUsage[]>;
  oneItemSelected$: Observable<boolean>;
  noItemsSelected$: Observable<boolean>;
  itToolUsageSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentItToolUsageSearchBy) currentItToolUsageSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_KPI_IT_TOOL_USAGE)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_KPI_IT_TOOL_USAGE)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_IT_TOOL_USAGE)) response$: Observable<WebApiResponse<KpiITToolUsage[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_KPI_IT_TOOL_USAGE)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_KPI_IT_TOOL_USAGE)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  @Select(AppDataState.taskExecuting(NgxsActionName.IMPORT_KPI_IT_TOOL_USAGE_DATA)) importing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.IMPORT_KPI_IT_TOOL_USAGE_DATA)) importResponse$: Observable<ImportResult[]>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_KPI_IT_TOOL_USAGE_DATA)) importErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_KPI_IT_TOOL_USAGE;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentItToolUsageSearchBy$.pipe(take(1)).subscribe((searchCriteria) => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map((errorResponse) => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.data)
    );
    this.totalCount$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.totalCount)
    );

    var kpis$ = this.kpiAllData$.pipe(
      filter(Boolean),
      map((c) => c.data),
      filter(Boolean),
      map((kpi) => kpi.filter((item) => item.kpiCategory.kpiCategoryId === SiteConfigHelper.GetKpiCategoryIdMapping().ITToolUsage).flatMap((item) => item))
    );
    this.itToolUsageSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$, kpis$, this.userCountry$]).pipe(
      map((c) => GetITToolUsageSearchByList(c))
    );

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.DeleteKpiITToolUsage), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$))
      .subscribe(([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.ImportKpiITToolUsageData), withLatestFrom(this.importResponse$, this.importErrorResponse$, this.profileMe$), takeUntil(this.destroy$))
      .subscribe(([_, importResponse, importErrorResponse, profileMe]) =>
        this.showImportResult(importResponse, importErrorResponse, "ml_143952", "ml_143950", profileMe, this.onSearchWithLastModifyPageFilter)
      );
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetKpiITToolUsage());
  };

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiITToolUsage(null));
  };

  onSearchWithLastModifyPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiITToolUsage(CreatePageFilterSettingWithLstChangeOnSort(this.functionName)));
  };

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_KPI_IT_TOOL_USAGE;
    this.store.dispatch(new AppDataAction.GetKpiITToolUsage(info));
  };

  onEdit = () => {
    this.dialog.open(ITToolUsageEditModalDialogComponent, {
      width: "500px",
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true,
    });
  };

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.data !== ModalDialogResult.YES) {
          return;
        }

        this.store.dispatch(new DeleteKpiITToolUsage(this.selectedItems.map((item) => item.kpiITToolUsage_Id)));
      });
  };

  onImport = (file: File) => {
    this.excelImportService.OnFileSlected(
      file,
      [ExcelSheetName.EVCR_COMPLIANCE, ExcelSheetName.APT_COMPLIANCE, ExcelSheetName.SAFETY_MEETING_COMPLIANCE],
      (fileData) => {
        var importData = this.csvService.GetImportObjectFromCsvData(CsvImportType.IT_TOOLS_USAGE, fileData);
        if (importData.length === 0) {
          this.showImportEmptyDataResult();
        } else {
          this.store.dispatch(new ImportKpiITToolUsageData(importData));
        }
      },
      this.showImportEmptyDataResult
    );
  };

  rowSelected = (data: KpiITToolUsage[]) => {
    this.selectedItems = data ?? [];
  };
}
